import { IonIcon } from '@ionic/react';
import { chevronBackOutline } from 'ionicons/icons';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  AppLink,
  HouseService,
  PdfLink,
  UrlLink,
  WP_HouseMainPage,
} from '../../model/models';
import { ModelsService } from '../../services/ModelsService';
import Faq from '../faq/Faq';
import Form from '../forms/Forms';
import GForm from '../gforms/GForm';
import HTMLContent from '../globals/HTMLContent';
import { SanitizeHTML } from '../globals/SanitizeHTML';
import {
  AppLinkDiv,
  AppLinkIcon,
  AppLinkPdf,
  AppLinkTitle,
  AppLinkUrl,
  APP_LINK_ICONS,
  ServiceAppLink,
} from '../services/serviceContent/subContent/AppLink';

interface ServicesProps {
  services: HouseService[];
}
interface ServiceProps {
  service: HouseService;
}

interface ServicesTabProps extends ServicesProps {
  slugs: string[];
  houseMainPage: WP_HouseMainPage;
}
/**
 * Services Tab
 * @param props
 * @returns
 */
export const ServicesTab: React.FC<ServicesTabProps> = ({
  services,
  slugs,
  houseMainPage,
}) => {
  const currentSlug = '/house/services/' + slugs.join('/');
  const filter: IServicesFilter = (service) =>
    getFullSlug(service) === currentSlug;
  return slugs.length > 0 ? (
    <Services services={services} filter={filter} />
  ) : (
    <div className="w-full text-left mt-2 px-2 md:mt-5">
      <Link
        className="hidden text-sm items-center font-bold text-primary mb-8 md:flex"
        to="/house"
      >
        <IonIcon className="text-primary mr-2" icon={chevronBackOutline} />
        <FormattedMessage id="Go back" />
      </Link>
      <h1 className="border-after font-semibold mb-8 text-xl inline-block ">
        <FormattedMessage id="House Services" />
      </h1>
      <ul className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {services
          ? services
              .filter((service) => service.parent === houseMainPage.id)
              .map((service: HouseService, index) => {
                return <ServiceItem key={index} service={service} />;
              })
          : ''}
      </ul>
    </div>
  );
};

export const ServicesTabDesktop: React.FC<ServicesTabProps> = ({
  services,
  slugs,
  houseMainPage,
}) => {
  const currentSlug = '/house/services/' + slugs.join('/');
  const filter: IServicesFilter = (service) =>
    getFullSlug(service) === currentSlug;
  return slugs.length > 0 ? (
    <Services services={services} filter={filter} />
  ) : (
    <div className="w-full text-left mt-2 bg-white bg-opacity-60 p-7 border-0 rounded-3xl">
      <div className="flex flex-row justify-between items-center mb-8 ">
        <h1 className="font-semibold text-xl inline-block m-0">
          <FormattedMessage id="House Services" />
        </h1>
        <Link
          to="/house/services"
          className="text-primary font-bold text-xs m-0"
        >
          <FormattedMessage id="See more" />
        </Link>
      </div>
      <ul className="flex flex-col">
        {services
          ? services
              .filter((service) => service.parent === houseMainPage.id)
              .slice(0, 2)
              .map((service: HouseService, index) => {
                return <ServiceItemDesktop key={index} service={service} />;
              })
          : ''}
      </ul>
    </div>
  );
};

/**
 * Service Breadcrumb
 * @param props
 * @returns
 */
interface BreadcrumbItem {
  label: string;
  link: string;
}

const ServiceBreadcrumb: React.FC<HouseServiceProps> = ({
  service,
  services,
}) => {
  const intl = useIntl();

  const servicesPath: BreadcrumbItem[] = (
    ModelsService.getAncestors(service, services) as HouseService[]
  )
    .reverse()
    .map((s) => ({ label: s.title.rendered, link: getFullSlug(s) }));
  const items = [
    { label: intl.formatMessage({ id: 'Services' }), link: '/house/services' },
  ].concat(servicesPath);

  // index of last item
  const last = items.length - 1;

  const Separator = () => <span className="mx-2">&gt;</span>;

  return (
    <ul className="flex text-gray-500 text-xs lg:text-base px-2 pl-4 md:mt-5">
      {items.map((item, idx) => {
        const isLast = idx === last;
        return (
          <li
            key={idx}
            className={
              'inline-flex items-center ' +
              (isLast ? '' : 'text-primary font-bold')
            }
          >
            <Link to={item.link}>
              <SanitizeHTML html={item.label} />
            </Link>
            {!isLast && <Separator />}
          </li>
        );
      })}
    </ul>
  );
};

interface HouseServiceProps {
  service: HouseService;
  services: HouseService[];
}
/**
 * Service page level > 1
 * @param props
 * @returns
 */
const Service: React.FC<HouseServiceProps> = ({ service, services }) => {
  const childs = services.filter((s) => s.parent === service.id);

  return (
    <div className="w-full">
      <ServiceBreadcrumb service={service} services={services} />
      {service.acf.icon && (
        <div className=" p-9 flex justify-center items-center w-0 h-0 text-3xl bg-primary text-white rounded-full mt-9 mx-auto">
          <i className={service.acf.icon.class} aria-hidden="true"></i>
        </div>
      )}

      <SanitizeHTML
        tag="h3"
        html={service.title.rendered}
        className="mt-6 text-black font-medium text-2xl mb-2"
      />

      <div className="styled-content">
        <HTMLContent html={service.content.rendered} />
      </div>
      <ul className="">
        {service.acf.links &&
          Object.values(service.acf.links).map((link: AppLink, index) => (
            <ServiceAppLink key={index}>
              {link.acf_fc_layout === 'pdf' && (
                <AppLinkPdf link={link as PdfLink}>
                  <AppLinkDiv>
                    <AppLinkTitle>{link.name}</AppLinkTitle>
                    <AppLinkIcon link={link} />
                  </AppLinkDiv>
                </AppLinkPdf>
              )}
              {link.acf_fc_layout === 'external_link' && (
                <AppLinkUrl link={link as UrlLink}>
                  <AppLinkDiv>
                    <AppLinkTitle>{link.name}</AppLinkTitle>
                    <AppLinkIcon link={link} />
                  </AppLinkDiv>
                </AppLinkUrl>
              )}
            </ServiceAppLink>
          ))}
      </ul>
      <ul className="">
        {childs.map((child: HouseService, index: number) => (
          <ServiceAppLink key={index}>
            <AppServiceLink service={child}>
              <AppLinkDiv>
                <i className={APP_LINK_ICONS['service']} aria-hidden="true"></i>
                <AppLinkTitle>{child.title.rendered}</AppLinkTitle>
              </AppLinkDiv>
            </AppServiceLink>
          </ServiceAppLink>
        ))}
      </ul>
      <Faq faq={service.acf.faq} />
      <Form postId={service.id} form={service.acf.form} />
      {service.acf.gform?.gform_activation === true &&
        service.acf.gform.gform && <GForm form={service.acf.gform.gform} />}
    </div>
  );
};

const getFullSlug = (service: HouseService): string => {
  const paths: string[] = new URL(service.link).pathname.split('/').slice(3); //.filter((it) => it)
  const link = paths ? '/house/services/' + paths.join('/') : '#';
  return link.substr(0, link.length - 1);
};

const AppServiceLink: React.FC<ServiceProps> = ({ service, children }) => {
  const link = getFullSlug(service);
  return <Link to={link}>{children}</Link>;
};

interface IServicesFilter {
  (service: HouseService): boolean;
}

interface ServicesFilterProps extends ServicesProps {
  filter: IServicesFilter;
}

/**
 * Services list
 * @param props
 * @returns
 */
export const Services: React.FC<ServicesFilterProps> = ({
  services,
  filter,
}) => {
  return (
    <>
      {services.filter(filter).map((service: HouseService, index: number) => (
        <Service key={index} service={service} services={services} />
      ))}
    </>
  );
};

/**
 * Service item
 * @param props
 * @returns
 */
export const ServiceItem: React.FC<ServiceProps> = ({ service }) => {
  return (
    <li className="col-span-1 flex flex-col text-left bg-white border border-primary border-opacity-40 rounded-xl">
      <Link to={'/house/services/' + service.slug}>
        <div className="flex-1 flex flex-col p-4 py-5">
          {service.acf.icon && (
            <div className=" p-5 flex justify-center items-center w-0 h-0 text-xl bg-primary text-white rounded-full">
              <i className={service.acf.icon.class} aria-hidden="true"></i>
            </div>
          )}
          <SanitizeHTML
            tag="h3"
            html={service.title.rendered}
            className="mt-6 text-black font-medium text-sm mb-2"
          />
        </div>
      </Link>
    </li>
  );
};
export const ServiceItemDesktop: React.FC<ServiceProps> = ({ service }) => {
  return (
    <li className="col-span-1 flex flex-row items-center text-left">
      <Link to={'/house/services/' + service.slug}>
        <div className="flex-1 flex flex-row items-center p-4 py-5 pl-0">
          {service.acf.icon && (
            <div className=" p-5 flex justify-center items-center w-0 h-0 text-xl bg-primary text-white rounded-full">
              <i className={service.acf.icon.class} aria-hidden="true"></i>
            </div>
          )}
          <SanitizeHTML
            tag="h3"
            html={service.title.rendered}
            className="text-black font-medium text-sm ml-3"
          />
        </div>
      </Link>
    </li>
  );
};
